import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronsUpDown } from "lucide-react"
import * as React from "react"
import { useGlobalSubstanceList } from "../../../api/substancesApi"

interface SubstancePickerProps {
  /** Label shown on the trigger — the currently chosen substance, or a prompt. */
  triggerLabel: string
  onSelect: (substanceId: string, label: string) => void
  className?: string
}

/**
 * Searchable single-select substance combobox, filtered by name/CAS/EC
 * server-side against the full universal registry (not just this org's
 * already-used substances) — modeled on ArticlePicker.tsx. Additional pages
 * stay reachable when a broad search matches more than 50 rows.
 */
export function SubstancePicker({
  triggerLabel,
  onSelect,
  className,
}: SubstancePickerProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [q, setQ] = React.useState("")
  const debouncedQ = useDebouncedValue(q, 250)

  const {
    data: substancePages,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGlobalSubstanceList({
    q: debouncedQ || undefined,
    limit: 50,
  })

  const substances = React.useMemo(
    () => substancePages?.pages.flatMap((p) => p.items) ?? [],
    [substancePages],
  )

  const handleSelect = React.useCallback(
    (substanceId: string) => {
      const substance = substances.find((s) => s.id === substanceId)
      if (!substance) return
      const label = substance.casNumber
        ? `${substance.name} (${substance.casNumber})`
        : substance.name
      onSelect(substance.id, label)
      setOpen(false)
      setQ("")
    },
    [substances, onSelect],
  )

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setQ("")
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex h-9 w-full items-center justify-between gap-1 rounded-md border border-grey4 bg-grey2 px-3 text-[13px] text-white hover:bg-primary/4",
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown size={13} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={translate("MC_MD_LINK_SUBSTANCE_SEARCH")}
            value={q}
            onValueChange={setQ}
          />
          <CommandList className="max-h-[260px]">
            {!isFetching && substances.length === 0 && (
              <CommandEmpty>
                {translate("MC_MD_LINK_SUBSTANCE_SEARCH_NONE")}
              </CommandEmpty>
            )}
            {substances.length > 0 && (
              <CommandGroup className="overflow-y-auto">
                {substances.map((substance) => (
                  <CommandItem
                    key={substance.id}
                    value={substance.id}
                    onSelect={handleSelect}
                    className="text-[13px]"
                  >
                    <span className="font-mono">
                      {substance.casNumber ?? substance.ecNumber ?? "—"}
                    </span>
                    <span className="ml-1.5 truncate text-grey5">
                      {substance.name}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {hasNextPage && (
              <CommandGroup>
                <CommandItem
                  value="load-more-substances"
                  className="justify-center text-[13px] text-grey5"
                  disabled={isFetchingNextPage}
                  onSelect={() => void fetchNextPage()}
                >
                  {translate("MC_DECL_LOAD_MORE")}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
